// src/services/payroll.service.js
import { api } from './api';

class PayrollService {
    /**
     * Get my payslips
     * BUG-006 FIX: Removed silent fallback to localStorage
     */
    async getMyPayslips(filters = {}) {
        try {
            const params = {};
            if (filters.year) params.year = filters.year;
            if (filters.month) params.month = filters.month;
            if (filters.status) params.status = filters.status;

            const response = await api.get('/api/payslips/my-payslips', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching payslips:', error);
            // Don't silently fall back to localStorage - throw error so UI can handle it
            throw new Error(`Failed to fetch payslips: ${error.response?.data?.message || error.message}`);
        }
    }

    /**
     * Get salary structure
     */
    async getSalaryStructure() {
        try {
            const response = await api.get('/api/payroll/salary-structure');
            return response.data.salaryStructure || null;
        } catch (error) {
            // Salary structure may not be configured yet
            if (error?.response?.status !== 404) {
                console.warn('Salary structure unavailable:', error?.response?.status || error.message);
            }
            return null;
        }
    }

    /**
     * Get tax declaration
     */
    async getTaxDeclaration(year) {
        try {
            const params = year ? { year } : {};
            const response = await api.get('/api/payroll/tax-declaration', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching tax declaration:', error);
            return null;
        }
    }

    /**
     * Submit tax declaration
     */
    async submitTaxDeclaration(declarationData) {
        try {
            const response = await api.post('/api/payroll/tax-declaration', declarationData);
            return response.data;
        } catch (error) {
            console.error('Error submitting tax declaration:', error);
            throw error;
        }
    }

    /**
     * Download payslip
     */
    async downloadPayslip(payslipId) {
        try {
            const response = await api.get(`/api/payroll/payslip/${payslipId}/download`, {
                responseType: 'blob'
            });

            // Create a download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `payslip_${payslipId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            return { success: true };
        } catch (error) {
            console.error('Error downloading payslip:', error);
            throw error;
        }
    }

    /**
     * Fallback: Get payroll data from localStorage
     */
    getLocalStoragePayroll() {
        // try {
        //     const stored = localStorage.getItem('hrportal_payroll');
        //     if (!stored) {
        //         return { payslips: [], current: null };
        //     }
        //
        //     const payrollData = JSON.parse(stored);
        //     // Convert localStorage format to backend format
        //     return {
        //         payslips: [],
        //         current: null,
        //         localData: payrollData
        //     };
        // } catch (error) {
        //     console.error('Error loading payroll from localStorage:', error);
        //     return { payslips: [], current: null };
        // }
    }

    /**
     * Save to localStorage for offline access
     */
    saveToLocalStorage(employeeId, payrollData) {
        try {
            const stored = localStorage.getItem('hrportal_payroll');
            const allPayroll = stored ? JSON.parse(stored) : {};

            allPayroll[employeeId] = payrollData;

            localStorage.setItem('hrportal_payroll', JSON.stringify(allPayroll));
        } catch (error) {
            console.error('Error saving payroll to localStorage:', error);
        }
    }

    // ============================================
    // PAYROLL PROCESSING API METHODS
    // ============================================

    /**
     * Get payroll periods for a company
     * @param {number} year - Optional year filter
     */
    async getPayrollPeriods(year = null) {
        try {
            const params = year ? { year } : {};
            const response = await api.get('/api/payroll/periods', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching payroll periods:', error);
            throw error;
        }
    }

    /**
     * Get payroll status for a specific period
     * @param {string} period - Period in format "Mon YYYY" (e.g., "Nov 2025")
     */
    async getPayrollStatus(period) {
        try {
            const response = await api.get(`/api/payroll/status`, { params: { period } });
            return response.data;
        } catch (error) {
            console.error('Error fetching payroll status:', error);
            // Return default status if API fails
            return {
                period,
                status: 'not_started',
                processed: false,
                locked: false,
                payslipsGenerated: false
            };
        }
    }

    /**
     * Process payroll for a given period
     * @param {string} period - Period in format "Mon YYYY" (e.g., "Nov 2025")
     * @param {Array} employeePayrollData - Array of employee payroll calculations
     */
    async processPayroll(period, employeePayrollData) {
        try {
            const response = await api.post('/api/payroll/process', {
                period,
                employees: employeePayrollData,
                processedAt: new Date().toISOString()
            });
            return response.data;
        } catch (error) {
            console.error('Error processing payroll:', error);
            throw new Error(`Failed to process payroll: ${error.response?.data?.message || error.message}`);
        }
    }

    /**
     * Save individual employee payslip
     * @param {string} period - Period in format "Mon YYYY"
     * @param {string} employeeId - Employee ID
     * @param {Object} payslipData - Payslip calculation data
     */
    async savePayslip(period, employeeId, payslipData) {
        try {
            const response = await api.post('/api/payroll/payslips', {
                period,
                employeeId,
                ...payslipData,
                generatedAt: new Date().toISOString()
            });
            return response.data;
        } catch (error) {
            console.error('Error saving payslip:', error);
            throw error;
        }
    }

    /**
     * Bulk save payslips for all employees in a period
     * @param {string} period - Period in format "Mon YYYY"
     * @param {Array} payslips - Array of payslip objects
     */
    async bulkSavePayslips(period, payslips) {
        try {
            const response = await api.post('/api/payroll/payslips/bulk', {
                period,
                payslips,
                generatedAt: new Date().toISOString()
            });
            return response.data;
        } catch (error) {
            console.error('Error bulk saving payslips:', error);
            throw new Error(`Failed to save payslips: ${error.response?.data?.message || error.message}`);
        }
    }

    /**
     * Lock payroll for a period (prevents further modifications)
     * @param {string} period - Period in format "Mon YYYY"
     */
    async lockPayroll(period) {
        try {
            const response = await api.post('/api/payroll/lock', {
                period,
                lockedAt: new Date().toISOString()
            });
            return response.data;
        } catch (error) {
            console.error('Error locking payroll:', error);
            throw new Error(`Failed to lock payroll: ${error.response?.data?.message || error.message}`);
        }
    }

    /**
     * Unlock payroll for a period (admin only, requires approval)
     * @param {string} period - Period in format "Mon YYYY"
     * @param {string} reason - Reason for unlocking
     */
    async unlockPayroll(period, reason) {
        try {
            const response = await api.post('/api/payroll/unlock', {
                period,
                reason,
                unlockedAt: new Date().toISOString()
            });
            return response.data;
        } catch (error) {
            console.error('Error unlocking payroll:', error);
            throw new Error(`Failed to unlock payroll: ${error.response?.data?.message || error.message}`);
        }
    }

    /**
     * Get payslips for a specific period
     * @param {string} period - Period in format "Mon YYYY"
     */
    async getPayslipsByPeriod(period) {
        try {
            const response = await api.get('/api/payroll/payslips', { params: { period } });
            return response.data;
        } catch (error) {
            console.error('Error fetching payslips by period:', error);
            throw error;
        }
    }

    /**
     * Get payroll summary for a period
     * @param {string} period - Period in format "Mon YYYY"
     */
    async getPayrollSummary(period) {
        try {
            const response = await api.get('/api/payroll/summary', { params: { period } });
            return response.data;
        } catch (error) {
            console.error('Error fetching payroll summary:', error);
            throw error;
        }
    }

    /**
     * Validate payroll data before processing
     * @param {string} period - Period in format "Mon YYYY"
     * @param {Array} employeePayrollData - Array of employee payroll calculations
     */
    async validatePayroll(period, employeePayrollData) {
        try {
            const response = await api.post('/api/payroll/validate', {
                period,
                employees: employeePayrollData
            });
            return response.data;
        } catch (error) {
            console.error('Error validating payroll:', error);
            throw error;
        }
    }

    // ============================================
    // ENHANCED PAYROLL API METHODS (For 1000+ employees)
    // ============================================

    /**
     * Get validation summary with smart defaults
     * Returns categorized employees: auto-validated, needs-review, has-issues
     * @param {string} period - Period in format "Mon YYYY"
     * @returns {Object} Summary with counts and employee lists per category
     */
    async getValidationSummary(period) {
        try {
            const response = await api.get('/api/payroll/validation/summary', {
                params: { period }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching validation summary:', error);
            throw new Error(`Failed to get validation summary: ${error.response?.data?.error || error.message}`);
        }
    }

    /**
     * Get paginated employees for payroll with validation status
     * @param {string} period - Period in format "Mon YYYY"
     * @param {Object} options - Pagination and filter options
     * @returns {Object} Paginated employee list with validation status
     */
    async getPaginatedEmployeesForPayroll(period, options = {}) {
        try {
            const {
                page = 1,
                limit = 50,
                status = 'all', // all, auto_validated, needs_review, has_issues
                department = null,
                search = null
            } = options;

            const params = {
                period,
                page,
                limit,
                status
            };

            if (department) params.department = department;
            if (search) params.search = search;

            const response = await api.get('/api/payroll/employees', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching paginated employees:', error);
            throw new Error(`Failed to get employees: ${error.response?.data?.error || error.message}`);
        }
    }

    /**
     * Bulk validate employees
     * @param {Array} employeeIds - Array of employee IDs
     * @param {string} action - 'approve', 'flag', or 'skip'
     * @param {string} notes - Optional notes
     * @param {string} period - Period in format "Mon YYYY" (e.g., "Nov 2025")
     * @returns {Object} Result with success/failure counts
     */
    async bulkValidateEmployees(employeeIds, action, notes = '', period = null) {
        try {
            const response = await api.post('/api/payroll/validation/bulk', {
                employeeIds,
                action,
                notes,
                period
            });
            return response.data;
        } catch (error) {
            console.error('Error bulk validating employees:', error);
            throw new Error(`Failed to bulk validate: ${error.response?.data?.error || error.message}`);
        }
    }

    /**
     * Start async payroll processing for large batches
     * @param {string} period - Period in format "Mon YYYY"
     * @param {number} batchSize - Number of employees per batch (default 50)
     * @returns {Object} Job info with jobId for progress tracking
     */
    async startAsyncProcessing(period, batchSize = 50) {
        try {
            const response = await api.post('/api/payroll/process/start', {
                period,
                batchSize
            });
            return response.data;
        } catch (error) {
            console.error('Error starting async processing:', error);
            throw new Error(`Failed to start processing: ${error.response?.data?.error || error.message}`);
        }
    }

    /**
     * Get processing progress for a job
     * @param {string} jobId - Job ID returned from startAsyncProcessing
     * @returns {Object} Progress info with status, percentage, ETA
     */
    async getProcessingProgress(jobId) {
        try {
            const response = await api.get(`/api/payroll/process/progress/${jobId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching processing progress:', error);
            throw new Error(`Failed to get progress: ${error.response?.data?.error || error.message}`);
        }
    }

    /**
     * Get departments list for filtering
     * @returns {Object} List of unique departments
     */
    async getDepartments() {
        try {
            const response = await api.get('/api/payroll/departments');
            return response.data;
        } catch (error) {
            console.error('Error fetching departments:', error);
            return { success: true, departments: [] }; // Graceful fallback
        }
    }

    /**
     * Poll processing progress until complete or failed
     * @param {string} jobId - Job ID to poll
     * @param {Function} onProgress - Callback for progress updates
     * @param {number} interval - Polling interval in ms (default 1000)
     * @returns {Promise} Resolves when processing completes
     */
    async pollProcessingProgress(jobId, onProgress, interval = 1000) {
        return new Promise((resolve, reject) => {
            const poll = async () => {
                try {
                    const result = await this.getProcessingProgress(jobId);

                    if (onProgress) {
                        onProgress(result.job);
                    }

                    if (result.job.status === 'completed') {
                        resolve(result.job);
                    } else if (result.job.status === 'failed') {
                        reject(new Error(result.job.error || 'Processing failed'));
                    } else {
                        setTimeout(poll, interval);
                    }
                } catch (error) {
                    reject(error);
                }
            };

            poll();
        });
    }

    // ============================================
    // OT CONFIGURATION API METHODS
    // ============================================

    /**
     * Get OT (Overtime) configuration
     * @returns {Object} OT configuration with rates and settings
     */
    async getOTConfig() {
        try {
            const response = await api.get('/api/payroll/config/ot');
            return response.data;
        } catch (error) {
            console.error('Error fetching OT config:', error);
            // Return default config on error
            return {
                success: true,
                config: {
                    enabled: false,
                    rates: { regular: 1.5, holiday: 2.0, weekly_off: 2.0 },
                    threshold: 8,
                    calculation_base: 'basic'
                }
            };
        }
    }

    /**
     * Save OT configuration
     * @param {Object} config - OT configuration object
     * @returns {Object} Updated configuration
     */
    async saveOTConfig(config) {
        try {
            const response = await api.post('/api/payroll/config/ot', config);
            return response.data;
        } catch (error) {
            console.error('Error saving OT config:', error);
            throw new Error(`Failed to save OT config: ${error.response?.data?.error || error.message}`);
        }
    }

    // ============================================
    // INCENTIVES API METHODS
    // ============================================

    /**
     * Get incentives for a period or employee
     * @param {Object} options - Filter options (period, employee_id)
     * @returns {Object} List of incentives
     */
    async getIncentives(options = {}) {
        try {
            const { period, employeeId } = options;
            const params = {};
            if (period) params.period = period;
            if (employeeId) params.employee_id = employeeId;

            const response = await api.get('/api/payroll/incentives', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching incentives:', error);
            return { success: true, incentives: [] };
        }
    }

    /**
     * Save an incentive for an employee
     * @param {Object} incentiveData - Incentive data
     * @returns {Object} Saved incentive
     */
    async saveIncentive(incentiveData) {
        try {
            const response = await api.post('/api/payroll/incentives', incentiveData);
            return response.data;
        } catch (error) {
            console.error('Error saving incentive:', error);
            throw new Error(`Failed to save incentive: ${error.response?.data?.error || error.message}`);
        }
    }

    /**
     * Delete an incentive
     * @param {string} incentiveId - Incentive ID to delete
     * @returns {Object} Success response
     */
    async deleteIncentive(incentiveId) {
        try {
            const response = await api.delete(`/api/payroll/incentives/${incentiveId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting incentive:', error);
            throw new Error(`Failed to delete incentive: ${error.response?.data?.error || error.message}`);
        }
    }

    // ============================================
    // HOLIDAYS & WORKING DAYS API METHODS
    // ============================================

    /**
     * Get holidays for a specific month/year
     * @param {number} year - Year
     * @param {number} month - Month (1-12)
     * @returns {Object} List of holidays
     */
    async getHolidays(year, month) {
        try {
            const response = await api.get('/api/holidays', {
                params: { year, month }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching holidays:', error);
            return { success: true, holidays: [] };
        }
    }

    /**
     * Get employee OT hours from attendance
     * @param {string} period - Period in format "Mon YYYY"
     * @param {string} employeeId - Optional employee ID for single employee
     * @returns {Object} OT hours data
     */
    async getEmployeeOTHours(period, employeeId = null) {
        try {
            const params = { period };
            if (employeeId) params.employee_id = employeeId;

            const response = await api.get('/api/attendance/overtime', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching employee OT hours:', error);
            return { success: true, employees: [] };
        }
    }

    /**
     * Get OT decisions for a payroll period
     * Returns decisions made via OTValidationWizard (pay_ot, dont_consider, grant_compoff, ignore)
     * @param {number} month - Month (1-12)
     * @param {number} year - Year
     * @returns {Object} OT decisions grouped by employee
     */
    async getOTDecisions(month, year) {
        try {
            const response = await api.get('/api/payroll/ot-decisions', {
                params: { month, year }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching OT decisions:', error);
            return { success: true, decisions: {} };
        }
    }

    // ============================================
    // BULK PAYSLIP PDF GENERATION
    // ============================================

    /**
     * Generate payslip PDF for a single employee
     * @param {Object} payslipData - Payslip data with employee info
     * @param {string} templateId - Template ID (pay-standard, pay-detailed, pay-modern)
     * @returns {Object} PDF URL and metadata
     */
    async generatePayslipPDF(payslipData, templateId = 'pay-standard') {
        try {
            const response = await api.post('/api/documents/generate-pdf', {
                templateName: 'payslip',
                templateId,
                data: payslipData,
                metadata: {
                    employeeName: payslipData.employeeName,
                    employeeCode: payslipData.employeeCode,
                    period: payslipData.period
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error generating payslip PDF:', error);
            throw new Error(`Failed to generate PDF: ${error.response?.data?.error || error.message}`);
        }
    }

    /**
     * Bulk generate payslip PDFs with progress tracking
     * @param {string} period - Period in format "Mon YYYY"
     * @param {Array} employees - Array of employee payslip data
     * @param {string} templateId - Template ID
     * @param {Function} onProgress - Progress callback (current, total, employee)
     * @returns {Object} Results with success/failure counts and URLs
     */
    async bulkGeneratePayslipPDFs(period, employees, templateId = 'pay-standard', onProgress = null) {
        const results = {
            success: [],
            failed: [],
            total: employees.length
        };

        for (let i = 0; i < employees.length; i++) {
            const emp = employees[i];

            try {
                // Call progress callback
                if (onProgress) {
                    onProgress({
                        current: i + 1,
                        total: employees.length,
                        employee: emp,
                        percentage: Math.round(((i + 1) / employees.length) * 100)
                    });
                }

                // Generate PDF
                const pdfResult = await this.generatePayslipPDF({
                    ...emp,
                    period
                }, templateId);

                results.success.push({
                    employeeId: emp.id || emp.employeeId,
                    employeeName: emp.name || emp.employeeName,
                    pdfUrl: pdfResult.pdfUrl
                });
            } catch (error) {
                results.failed.push({
                    employeeId: emp.id || emp.employeeId,
                    employeeName: emp.name || emp.employeeName,
                    error: error.message
                });
            }

            // Small delay to prevent overwhelming the server
            await new Promise(resolve => setTimeout(resolve, 100));
        }

        return results;
    }

    /**
     * Download payslip PDF by URL
     * @param {string} pdfUrl - PDF URL to download
     * @param {string} filename - Filename for download
     */
    async downloadPayslipByUrl(pdfUrl, filename = 'payslip.pdf') {
        try {
            const response = await api.get(pdfUrl, { responseType: 'blob' });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            return { success: true };
        } catch (error) {
            console.error('Error downloading payslip:', error);
            throw error;
        }
    }

    /**
     * Download all payslips as ZIP
     * @param {string} period - Period in format "Mon YYYY"
     * @returns {Blob} ZIP file
     */
    async downloadAllPayslipsAsZip(period) {
        try {
            const response = await api.get('/api/payroll/payslips/download-all', {
                params: { period },
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `payslips_${period.replace(' ', '_')}.zip`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            return { success: true };
        } catch (error) {
            console.error('Error downloading payslips ZIP:', error);
            throw new Error(`Failed to download payslips: ${error.response?.data?.error || error.message}`);
        }
    }

    // ============================================
    // FIREBASE PAYSLIP STORAGE API METHODS
    // ============================================

    /**
     * Generate and store payslips in Firebase Storage for a locked payroll
     * This is called when locking payroll to create permanent PDF copies
     * @param {Object} payrollData - Payroll period info (month, year, periodName)
     * @param {Array} employees - Array of employee payroll calculation data
     * @returns {Object} Results with generated payslips
     */
    async generateAndStorePayslips(payrollData, employees) {
        try {
            console.log(`📋 Generating ${employees.length} payslips for ${payrollData.periodName}...`);

            const response = await api.post('/api/payslips/generate-bulk', {
                payrollData,
                employees
            });

            console.log(`✅ Payslips generated: ${response.data.generatedCount} success, ${response.data.errorCount} errors`);
            return response.data;
        } catch (error) {
            console.error('Error generating payslips:', error);
            throw new Error(`Failed to generate payslips: ${error.response?.data?.message || error.message}`);
        }
    }

    /**
     * Get stored payslips from Firebase for a period
     * @param {number} year - Year
     * @param {number} month - Month (1-12)
     * @returns {Object} List of payslips with download URLs
     */
    async getStoredPayslips(year, month) {
        try {
            const params = {};
            if (year) params.year = year;
            if (month) params.month = month;

            const response = await api.get('/api/payslips', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching stored payslips:', error);
            return { success: false, payslips: [], error: error.message };
        }
    }

    /**
     * Get payslips summary for a period
     * @param {number} year - Year
     * @param {number} month - Month (1-12)
     * @returns {Object} Summary with counts and totals
     */
    async getPayslipsSummary(year, month) {
        try {
            const response = await api.get('/api/payslips/summary', {
                params: { year, month }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching payslips summary:', error);
            return { success: false, summary: null };
        }
    }

    /**
     * Download a single payslip PDF from Firebase
     * @param {string} payslipId - Payslip ID
     * @param {string} fileName - Optional filename for download
     * @returns {Object} Download URL or initiates download
     */
    async downloadStoredPayslip(payslipId, fileName = null) {
        try {
            const response = await api.get(`/api/payslips/${payslipId}/download`);

            if (response.data.success && response.data.pdfUrl) {
                // Open PDF in new tab or trigger download
                const link = document.createElement('a');
                link.href = response.data.pdfUrl;
                link.target = '_blank';
                link.setAttribute('download', fileName || response.data.fileName || `payslip_${payslipId}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();

                return { success: true, pdfUrl: response.data.pdfUrl };
            }

            throw new Error('PDF URL not available');
        } catch (error) {
            console.error('Error downloading stored payslip:', error);
            throw new Error(`Failed to download payslip: ${error.response?.data?.message || error.message}`);
        }
    }

    /**
     * Get payslip details by ID
     * @param {string} payslipId - Payslip ID
     * @returns {Object} Payslip details
     */
    async getPayslipDetails(payslipId) {
        try {
            const response = await api.get(`/api/payslips/${payslipId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching payslip details:', error);
            throw new Error(`Failed to fetch payslip: ${error.response?.data?.message || error.message}`);
        }
    }

    /**
     * Refresh payslip URL (for expired signed URLs)
     * @param {string} payslipId - Payslip ID
     * @returns {Object} Updated payslip with new URL
     */
    async refreshPayslipUrl(payslipId) {
        try {
            const response = await api.post(`/api/payslips/${payslipId}/refresh-url`);
            return response.data;
        } catch (error) {
            console.error('Error refreshing payslip URL:', error);
            throw new Error(`Failed to refresh URL: ${error.response?.data?.message || error.message}`);
        }
    }

    // ============================================
    // ASYNC PAYSLIP GENERATION API METHODS
    // ============================================

    /**
     * Start async payslip generation job
     * Returns jobId immediately, payslips are generated in background
     * @param {Object} payrollData - Payroll period info (month, year, periodName)
     * @param {Array} employees - Array of employee payroll calculation data
     * @returns {Object} Job info with jobId
     */
    async startPayslipGeneration(payrollData, employees) {
        try {
            console.log(`📋 Starting async payslip generation for ${employees.length} employees...`);

            const response = await api.post('/api/payslips/generate-bulk/start', {
                payrollData,
                employees
            });

            console.log(`✅ Payslip generation job started: ${response.data.jobId}`);
            return response.data;
        } catch (error) {
            console.error('Error starting payslip generation:', error);
            throw new Error(`Failed to start payslip generation: ${error.response?.data?.message || error.message}`);
        }
    }

    /**
     * Get progress of a payslip generation job
     * @param {string} jobId - Job ID from startPayslipGeneration
     * @returns {Object} Job progress info
     */
    async getPayslipJobProgress(jobId) {
        try {
            const response = await api.get(`/api/payslips/jobs/${jobId}/progress`);
            return response.data;
        } catch (error) {
            console.error('Error getting payslip job progress:', error);
            throw new Error(`Failed to get job progress: ${error.response?.data?.message || error.message}`);
        }
    }

    /**
     * Cancel a payslip generation job
     * @param {string} jobId - Job ID to cancel
     * @returns {Object} Cancellation result
     */
    async cancelPayslipJob(jobId) {
        try {
            const response = await api.post(`/api/payslips/jobs/${jobId}/cancel`);
            return response.data;
        } catch (error) {
            console.error('Error cancelling payslip job:', error);
            throw new Error(`Failed to cancel job: ${error.response?.data?.message || error.message}`);
        }
    }

    /**
     * Get active payslip generation jobs for company
     * @returns {Object} List of active jobs
     */
    async getActivePayslipJobs() {
        try {
            const response = await api.get('/api/payslips/jobs/active');
            return response.data;
        } catch (error) {
            console.error('Error getting active payslip jobs:', error);
            return { success: true, jobs: [], count: 0 };
        }
    }

    // ============================================
    // SALARY REVISION CONFIRMATION API METHODS
    // ============================================

    /**
     * Get salary revisions pending for a payroll period
     * @param {number} month - Month (1-12)
     * @param {number} year - Year
     * @returns {Object} List of pending salary revisions
     */
    async getSalaryRevisionsForPayroll(month, year) {
        try {
            const response = await api.get('/api/salary-revisions/payroll-period', {
                params: { month, year }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching salary revisions for payroll:', error);
            return { success: false, revisions: [] };
        }
    }

    /**
     * Confirm a salary revision for payroll processing
     * @param {string} revisionId - Salary revision ID
     * @param {number} month - Payroll month (1-12)
     * @param {number} year - Payroll year
     * @param {string} status - 'confirmed' or 'deferred'
     * @param {string} notes - Optional notes
     * @returns {Object} Confirmation result
     */
    async confirmSalaryRevision(revisionId, month, year, status = 'confirmed', notes = '') {
        try {
            const response = await api.post('/api/salary-revisions/confirm', {
                revisionId,
                month,
                year,
                status,
                notes
            });
            return response.data;
        } catch (error) {
            console.error('Error confirming salary revision:', error);
            throw new Error(`Failed to confirm salary revision: ${error.response?.data?.message || error.message}`);
        }
    }

    /**
     * Bulk confirm multiple salary revisions for payroll
     * @param {Array} revisionIds - Array of revision IDs
     * @param {number} month - Payroll month (1-12)
     * @param {number} year - Payroll year
     * @param {string} status - 'confirmed' or 'deferred'
     * @returns {Object} Bulk confirmation result
     */
    async bulkConfirmSalaryRevisions(revisionIds, month, year, status = 'confirmed') {
        try {
            const results = await Promise.all(
                revisionIds.map(revisionId =>
                    this.confirmSalaryRevision(revisionId, month, year, status)
                )
            );
            return {
                success: true,
                confirmed: results.filter(r => r.success).length,
                failed: results.filter(r => !r.success).length
            };
        } catch (error) {
            console.error('Error bulk confirming salary revisions:', error);
            throw error;
        }
    }

    /**
     * Get salary revision confirmations for a period
     * @param {number} month - Month (1-12)
     * @param {number} year - Year
     * @returns {Object} List of confirmations
     */
    async getSalaryRevisionConfirmations(month, year) {
        try {
            const response = await api.get('/api/salary-revisions/confirmations', {
                params: { month, year }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching salary revision confirmations:', error);
            return { success: false, confirmations: [] };
        }
    }

    // ============================================
    // AUDIT TRAIL API METHODS
    // ============================================

    /**
     * Get paginated audit logs with filters
     * @param {Object} params - Query parameters
     * @param {number} params.page - Page number (default 1)
     * @param {number} params.limit - Items per page (default 20)
     * @param {string} params.entityType - Filter by entity type
     * @param {string} params.action - Filter by action
     * @param {string} params.fromDate - Filter from date
     * @param {string} params.toDate - Filter to date
     * @param {string} params.search - Search term
     * @returns {Object} Paginated audit logs
     */
    async getAuditLogs(params = {}) {
        try {
            const response = await api.get('/api/payroll/audit/logs', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching audit logs:', error);
            return { success: false, logs: [], pagination: { page: 1, limit: 20, total: 0, totalPages: 0 } };
        }
    }

    /**
     * Get audit history for a specific entity
     * @param {string} entityType - Type of entity (adhoc_entry, arrear, etc.)
     * @param {string} entityId - Entity ID
     * @returns {Object} Audit history for the entity
     */
    async getEntityAuditHistory(entityType, entityId) {
        try {
            const response = await api.get(`/api/payroll/audit/entity/${entityType}/${entityId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching entity audit history:', error);
            return { success: false, history: [] };
        }
    }

    /**
     * Get audit summary statistics
     * @param {Object} params - Query parameters
     * @param {string} params.fromDate - Start date for summary
     * @param {string} params.toDate - End date for summary
     * @returns {Object} Summary statistics by entity type and action
     */
    async getAuditSummary(params = {}) {
        try {
            const response = await api.get('/api/payroll/audit/summary', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching audit summary:', error);
            return { success: false, summary: null };
        }
    }

    /**
     * Get payroll audit compliance report for a specific period
     * @param {number} month - Month (1-12)
     * @param {number} year - Year
     * @returns {Object} Compliance report data
     */
    async getAuditReport(month, year) {
        try {
            const response = await api.get('/api/payroll/audit/report', {
                params: { month, year }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching audit report:', error);
            return { success: false, report: [] };
        }
    }

    /**
     * Export audit logs with optional filters
     * @param {Object} params - Export parameters
     * @param {string} params.entityType - Filter by entity type
     * @param {string} params.action - Filter by action
     * @param {string} params.fromDate - Filter from date
     * @param {string} params.toDate - Filter to date
     * @returns {Object} Exported audit logs
     */
    async exportAuditLogs(params = {}) {
        try {
            const response = await api.get('/api/payroll/audit/export', { params });
            return response.data;
        } catch (error) {
            console.error('Error exporting audit logs:', error);
            return { success: false, logs: [] };
        }
    }

    // ============================================
    // PERFORMANCE OPTIMIZATION API METHODS
    // ============================================

    /**
     * Get consolidated payroll initialization data in a single API call
     * This reduces 11+ separate API calls to 1 call for ~70-80% faster load times
     *
     * @param {number} month - Month (1-12)
     * @param {number} year - Year
     * @param {Array} employeeIds - Optional array of employee IDs for attendance
     * @returns {Object} Consolidated payroll data including:
     *   - validationCategories
     *   - leaveRequests (filtered by month/year)
     *   - salaryRevisions (filtered by month/year)
     *   - ptExemptions
     *   - tdsDeclarations
     *   - adhocEntries (filtered by month/year)
     *   - arrears (filtered by month/year)
     *   - exitRequests (approved & pending)
     *   - payrollPeriods
     *   - attendance (bulk, grouped by employee_id)
     */
    async getPayrollInit(month, year, employeeIds = []) {
        try {
            console.log(`📊 Fetching consolidated payroll init data for ${month}/${year}...`);
            const startTime = Date.now();

            const params = { month, year };
            if (employeeIds && employeeIds.length > 0) {
                params.employee_ids = employeeIds.join(',');
            }

            const response = await api.get('/api/payroll/init', { params });

            const endTime = Date.now();
            console.log(`✅ Payroll init data fetched in ${endTime - startTime}ms`);

            return response.data;
        } catch (error) {
            console.error('Error fetching payroll init data:', error);
            // Return empty structure to allow graceful fallback
            return {
                success: false,
                data: {
                    validationCategories: [],
                    leaveRequests: [],
                    salaryRevisions: [],
                    ptExemptions: [],
                    tdsDeclarations: [],
                    adhocEntries: [],
                    arrears: [],
                    exitRequests: { approved: [], pending: [] },
                    payrollPeriods: [],
                    attendance: {}
                },
                error: error.message
            };
        }
    }

    /**
     * Get bulk attendance for multiple employees in a single API call
     * This replaces N individual attendance API calls with 1 call
     *
     * @param {number} month - Month (1-12)
     * @param {number} year - Year
     * @param {Array} employeeIds - Array of employee IDs
     * @returns {Object} Attendance data grouped by employee_id
     */
    async getBulkAttendance(month, year, employeeIds = []) {
        try {
            console.log(`📋 Fetching bulk attendance for ${employeeIds.length} employees...`);
            const startTime = Date.now();

            const params = { month, year };
            if (employeeIds && employeeIds.length > 0) {
                params.employee_ids = employeeIds.join(',');
            }

            const response = await api.get('/api/payroll/attendance/bulk', { params });

            const endTime = Date.now();
            console.log(`✅ Bulk attendance fetched in ${endTime - startTime}ms`);

            return response.data;
        } catch (error) {
            console.error('Error fetching bulk attendance:', error);
            return {
                success: false,
                attendance: {},
                error: error.message
            };
        }
    }

    // ============================================================
    // PAYROLL HOLDS  — withhold an employee's salary for a period
    // ============================================================

    /** List employees currently on payroll hold (org-wide). */
    async getEmployeesOnHold() {
        try {
            const response = await api.get('/api/payroll/holds');
            return response.data;
        } catch (error) {
            console.error('Error fetching employees on hold:', error);
            throw error;
        }
    }

    /**
     * Toggle an employee's payroll hold flag.
     * @param {string} employeeId
     * @param {{ payroll_hold: boolean, payroll_hold_reason?: string }} payload
     */
    async setPayrollHold(employeeId, payload) {
        try {
            const response = await api.put(`/api/payroll/holds/${employeeId}`, payload);
            return response.data;
        } catch (error) {
            console.error('Error setting payroll hold:', error);
            throw error;
        }
    }

    /**
     * Release an existing hold for a single payroll period (employee
     * stays on hold afterwards). Backend stores month/year in
     * payroll_hold_released_for_{month,year}.
     */
    async releaseHoldForPeriod(employeeId, { month, year }) {
        try {
            const response = await api.put(`/api/payroll/holds/${employeeId}/release`, { month, year });
            return response.data;
        } catch (error) {
            console.error('Error releasing hold for period:', error);
            throw error;
        }
    }

    // ── Period-specific holds (per (employee, month, year)) ──────

    async listPeriodHolds({ month, year } = {}) {
        const params = new URLSearchParams();
        if (month) params.append('month', month);
        if (year)  params.append('year', year);
        const response = await api.get(`/api/payroll/period-holds?${params.toString()}`);
        return response.data;
    }

    async listPeriodHoldsForEmployee(employeeId) {
        const response = await api.get(`/api/payroll/period-holds/employee/${employeeId}`);
        return response.data;
    }

    async createPeriodHold({ employeeId, month, year, reason }) {
        const response = await api.post('/api/payroll/period-holds', { employeeId, month, year, reason });
        return response.data;
    }

    async releasePeriodHold(holdId) {
        const response = await api.delete(`/api/payroll/period-holds/${holdId}`);
        return response.data;
    }

    /**
     * Regenerate a single employee's payslip for one month using client-computed
     * salary values. The backend updates the payroll_records row, re-issues the
     * PDF and upserts the payslips row.
     * @param {Object} payload - { employeeId, month, year, earnings, deductions,
     *   gross, totalDeductions, net, totalWorkingDays, daysWorked, lopDays }
     * @returns {Object} { success, before, after, payslipId, pdfUrl }
     */
    async regenerateSinglePayslip(payload) {
        const response = await api.post('/api/payslips/regenerate-single', payload);
        return response.data;
    }
}

export default new PayrollService();
